import React from 'react';
import { Box, ButtonBase, Collapse } from '@mui/material';
import { useIntercom } from 'react-use-intercom';
import type { PreApproveType } from '../../../../types/preloan/v3';
import { plr } from './tokens';
import { PlrBackLink, PlrButton } from './ui';
import {
  alertCircleIcon, arrowRightIcon, chevronDownIcon, homeIcon, personAddIcon,
} from './icons';
import analytics from '../../../../utils/analytics';
import getProductTypeLabels from '../../../../components/PreApprove/helpers';
import { decisionExplanation, HUMAN_REVIEW_MESSAGE } from '../legal';
import {
  estimatedDividendUf, formatPreApproveAmount, formatRate, formatUf, formatYears,
} from './results';

export function PreQualifiesChip() {
  return (
    <Box
      component="span"
      data-pw="preloan-result-chip"
      sx={{
        background: plr.mint,
        color: plr.mintInk,
        fontSize: { xs: 12.5, sm: 13 },
        fontWeight: 700,
        p: { xs: '4px 11px', sm: '4px 12px' },
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      Pre-calificas
    </Box>
  );
}

function Stat({ label, value }: { label: string, value: string }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0,
    }}
    >
      <Box component="span" sx={{ fontSize: { xs: 12.5, sm: 14 }, color: plr.heroLabel }}>{label}</Box>
      <Box
        component="b"
        sx={{ fontSize: { xs: 15.5, sm: 17 }, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {value}
      </Box>
    </Box>
  );
}

const divider = '1px solid rgba(220,213,247,0.25)';

// Como el flujo anterior (PreApproveCard): si la postulación ya se envió a otro partner.
function TransferredNote({ preApprove, color }: { preApprove: PreApproveType, color: string }) {
  if (!preApprove.isTransferrable || !preApprove.alreadyTransferred) return null;
  return (
    <Box component="span" sx={{ fontSize: { xs: 12.5, sm: 13.5 }, lineHeight: 1.45, color }}>
      {`¡Tu postulación ya ha sido enviada! Se te contactará de ${preApprove.toTransferPartnerName} para continuar el proceso.`}
    </Box>
  );
}

export function PreApprovalCard({ preApprove }: { preApprove: PreApproveType }) {
  // El dividendo estimado (UF) solo aplica al hipotecario.
  const dividend = preApprove.productType === 'mortgage' ? estimatedDividendUf(preApprove) : undefined;
  return (
    <Box
      component="section"
      aria-label="Tu pre-calificación"
      data-pw="preloan-pre-approval"
      sx={{
        background: plr.hero,
        color: '#FFFFFF',
        borderRadius: { xs: '20px', sm: '22px' },
        p: { xs: '18px 20px', sm: '22px 26px' },
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Box component="span" sx={{ fontSize: { xs: 14, sm: 15 }, color: plr.heroText }}>
        Podrías financiar hasta
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: { xs: 34, sm: 44 },
          lineHeight: 1.1,
          fontWeight: 800,
          letterSpacing: '-1px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatPreApproveAmount(preApprove)}
      </Box>
      {preApprove.isTransferrable && preApprove.toTransferPartnerName && (
        <Box component="span" sx={{ fontSize: { xs: 13.5, sm: 15 }, color: plr.heroText }}>
          {`Con ${preApprove.toTransferPartnerName}`}
        </Box>
      )}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '10px',
        mt: '12px',
        pt: '12px',
        borderTop: divider,
      }}
      >
        <Stat label="Plazo" value={formatYears(preApprove.maxPeriods)} />
        <Stat label="Tasa anual UF" value={formatRate(preApprove.rate)} />
        {dividend !== undefined && <Stat label="Dividendo est." value={formatUf(dividend, 1)} />}
      </Box>
      <TransferredNote preApprove={preApprove} color={plr.heroText} />
    </Box>
  );
}

// Resto de las preaprobaciones que mande el backend: misma información que
// las tarjetas del carrusel anterior (producto, monto, plazo y tasa), en formato compacto.
export function PreApprovalItem({ preApprove }: { preApprove: PreApproveType }) {
  return (
    <Box
      component="section"
      aria-label={getProductTypeLabels(preApprove.productType, preApprove.productSubtype)}
      data-pw={`preloan-pre-approval-${preApprove.productType}`}
      sx={{
        background: plr.surface,
        borderRadius: { xs: '18px', sm: '20px' },
        p: { xs: '14px 18px', sm: '16px 22px' },
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        color: plr.ink,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px',
      }}
      >
        <Box component="b" sx={{ fontSize: { xs: 14, sm: 15 } }}>
          {getProductTypeLabels(preApprove.productType, preApprove.productSubtype)}
        </Box>
        {preApprove.isTransferrable && preApprove.toTransferPartnerName && (
          <Box component="span" sx={{ fontSize: { xs: 12.5, sm: 13.5 }, color: plr.muted }}>
            {`Con ${preApprove.toTransferPartnerName}`}
          </Box>
        )}
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '10px',
        '& span': { color: plr.muted },
      }}
      >
        <Stat label="Hasta" value={formatPreApproveAmount(preApprove)} />
        <Stat label="Plazo" value={formatYears(preApprove.maxPeriods)} />
        <Stat label="Tasa anual UF" value={formatRate(preApprove.rate)} />
      </Box>
      <TransferredNote preApprove={preApprove} color={plr.body} />
    </Box>
  );
}

export function SimulationNote() {
  return (
    <Box
      component="p"
      sx={{
        m: '-4px 0 0', fontSize: { xs: 12, sm: 13 }, lineHeight: 1.45, color: plr.muted,
      }}
    >
      Simulación referencial. No es una aprobación: la decisión final la toma la institución
      que financia.
    </Box>
  );
}

function Bar({ own, extra }: { own: number, extra?: number }) {
  return (
    <Box
      component="span"
      sx={{
        flex: 1,
        height: { xs: 8, sm: 9 },
        borderRadius: '5px',
        background: 'rgba(255,255,255,0.85)',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Box
        component="span"
        sx={{ width: `${own}%`, background: plr.ink, borderRadius: extra !== undefined ? 0 : '5px' }}
      />
      {extra !== undefined && (
        <Box
          component="span"
          sx={{
            width: extra ? `${extra}%` : undefined,
            flex: extra ? undefined : 1,
            background: plr.stripes,
            borderRadius: '0 5px 5px 0',
          }}
        />
      )}
    </Box>
  );
}

Bar.defaultProps = { extra: undefined };

const barLabelSx = { width: { xs: 92, sm: 104 }, flexShrink: 0 };
const barValueSx = {
  width: { xs: 62, sm: 68 }, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap',
} as const;

const legendSwatchSx = {
  width: 18, height: 7, borderRadius: '4px', flexShrink: 0, display: 'inline-block',
} as const;

const stepNumberSx = {
  width: { xs: 24, sm: 26 },
  height: { xs: 24, sm: 26 },
  flexShrink: 0,
  borderRadius: '50%',
  background: '#FFFFFF',
  color: plr.accent,
  fontSize: 12.5,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 4px rgba(51,38,117,0.12)',
} as const;

// [validar textos con Producto y Legal]
const COMPLEMENT_STEPS = [
  {
    title: 'Invitas a un familiar',
    text: 'Tu cónyuge o pareja, tu padre o madre, un hijo o hija, u otra persona cercana.',
  },
  {
    title: 'Se evalúa con su ClaveÚnica',
    text: 'Le llega un link y da su propio consentimiento.',
  },
  {
    title: 'Volvemos a evaluar tu solicitud',
    text: 'Sumamos sus ingresos a los tuyos y te mostramos el resultado combinado.',
  },
];

type ComplementCardProps = {
  // approved: 4f (barras + leyenda). rejected: 4fb (cómo funciona, en 3 pasos).
  variant: 'approved' | 'rejected',
  amountLabel?: string,
  onOpen: () => void,
};

export function ComplementCard({ variant, amountLabel, onOpen }: ComplementCardProps) {
  const approved = variant === 'approved';
  const title = approved ? '¿Sumas los ingresos de un familiar?' : 'Suma los ingresos de un familiar';
  const body = approved
    ? 'Aumenta el monto de tu crédito invitándole a evaluarse con nosotros.'
    : 'Invítale a evaluarse con nosotros para sumar sus ingresos a tu solicitud.';
  return (
    <Box
      component="section"
      aria-labelledby="plr-complement-title"
      data-pw="complement-offer"
      sx={{
        background: plr.gradient,
        borderRadius: { xs: '20px', sm: '22px' },
        p: approved ? { xs: '16px', sm: '20px 22px' } : { xs: '18px 16px 16px', sm: '22px 24px' },
        display: 'flex',
        flexDirection: approved ? { xs: 'column', sm: 'row' } : 'column',
        flexWrap: 'wrap',
        alignItems: approved ? { xs: 'stretch', sm: 'center' } : 'stretch',
        gap: approved ? { xs: '12px', sm: '22px' } : { xs: '12px', sm: '14px' },
      }}
    >
      <Box sx={{
        flex: approved ? { xs: '0 0 auto', sm: '1 1 260px' } : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
      >
        <Box sx={{ display: 'flex', gap: { xs: '12px', sm: '14px' }, alignItems: 'flex-start' }}>
          <Box
            component="span"
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
              flexShrink: 0,
              borderRadius: '50%',
              background: '#FFFFFF',
              color: plr.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(51,38,117,0.12)',
            }}
          >
            {personAddIcon({ size: 19 })}
          </Box>
          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Box
              component="b"
              id="plr-complement-title"
              sx={{ fontSize: approved ? { xs: 16, sm: 17 } : { xs: 17, sm: 19 }, lineHeight: 1.3 }}
            >
              {title}
            </Box>
            <Box component="span" sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.45, color: plr.body }}>
              {body}
            </Box>
          </Box>
        </Box>
        {approved ? (
          <Box
            aria-hidden="true"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '7px',
              fontSize: { xs: 12, sm: 12.5 },
              color: plr.body,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box component="span" sx={barLabelSx}>Solo tú</Box>
              <Bar own={58} />
              {amountLabel && (
                <Box component="b" sx={{ ...barValueSx, color: plr.ink }}>{amountLabel}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box component="span" sx={barLabelSx}>Con un familiar</Box>
              <Bar own={58} extra={0} />
              {amountLabel && (
                <Box component="b" sx={{ ...barValueSx, color: plr.accent }}>+ su renta</Box>
              )}
            </Box>
            {/* Leyenda de colores de las barras. */}
            <Box
              data-pw="complement-legend"
              sx={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px', mt: '2px',
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box component="span" sx={{ ...legendSwatchSx, background: plr.ink }} />
                Tu renta
              </Box>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box component="span" sx={{ ...legendSwatchSx, background: plr.stripes }} />
                Renta de tu familiar
              </Box>
            </Box>
          </Box>
        ) : (
          // 4fb: sin endpoint del mínimo del proyecto no mostramos cuánto falta; explicamos
          // cómo funciona el complemento.
          <Box
            component="ol"
            data-pw="complement-steps"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '10px', sm: '12px' },
            }}
          >
            {COMPLEMENT_STEPS.map((step, i) => (
              <Box component="li" key={step.title} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Box component="span" sx={stepNumberSx}>{i + 1}</Box>
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <Box component="b" sx={{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1.35 }}>{step.title}</Box>
                  <Box component="span" sx={{ fontSize: { xs: 13, sm: 13.5 }, lineHeight: 1.45, color: plr.body }}>
                    {step.text}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <PlrButton
        testId="complement-offer-button"
        onClick={onOpen}
        sx={{
          height: approved ? { xs: 50, sm: 54 } : { xs: 52, sm: 56 },
          fontSize: approved ? 16 : 17,
          alignSelf: approved ? { xs: 'stretch', sm: 'center' } : { xs: 'stretch', sm: 'flex-start' },
          px: approved ? '26px' : '30px',
          boxShadow: '0 4px 12px rgba(41,29,94,0.25)',
        }}
      >
        Complementar mi renta
        {arrowRightIcon({ size: 18, strokeWidth: 2.2 })}
      </PlrButton>
      {!approved && (
        <Box component="span" sx={{ fontSize: { xs: 12, sm: 12.5 }, lineHeight: 1.45, color: plr.muted }}>
          El complemento no es una aprobación: decide la institución que financia.
        </Box>
      )}
    </Box>
  );
}

ComplementCard.defaultProps = { amountLabel: undefined };

export function InfoRow({ children }: { children: React.ReactNode }) {
  return (
    <Box
      data-pw="preloan-last-step-body"
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        fontSize: { xs: 13, sm: 14 },
        lineHeight: 1.45,
        color: plr.body,
        '& p': { m: 0 },
      }}
    >
      <Box component="span" sx={{ color: plr.accent, mt: '1px', display: 'flex' }}>
        {homeIcon({ size: 17, strokeWidth: 2.2 })}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>{children}</Box>
    </Box>
  );
}

export function TodoList({ items, compact = false }: {
  items: React.ReactNode[],
  compact?: boolean,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '8px' : { xs: '10px', sm: '12px' },
        p: compact ? 0 : '2px 2px 0',
      }}
    >
      <Box
        component={compact ? 'h3' : 'h2'}
        sx={{ m: 0, fontSize: compact ? { xs: 13, sm: 14 } : { xs: 15, sm: 17 }, fontWeight: 800 }}
      >
        Qué puedes hacer
      </Box>
      <Box
        component="ol"
        sx={{
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '8px' : '10px',
          fontSize: compact ? { xs: 13, sm: 14 } : { xs: 13.5, sm: 15 },
          lineHeight: 1.45,
          color: plr.body,
          // Neutraliza la regla global "li a" (menú) que vuelve los links bloques centrados.
          '& li a': {
            display: 'inline',
            p: 0,
            m: 0,
            textAlign: 'left',
            borderRadius: 0,
            color: plr.ink,
            textDecoration: 'underline',
            fontWeight: 700,
            transition: 'none',
          },
        }}
      >
        {items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box component="li" key={i} sx={{ display: 'flex', gap: { xs: '10px', sm: '12px' } }}>
            <Box
              component="span"
              sx={{
                width: { xs: 22, sm: 24 },
                height: { xs: 22, sm: 24 },
                flexShrink: 0,
                borderRadius: '50%',
                background: plr.chip,
                color: plr.accent,
                fontSize: 12,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {i + 1}
            </Box>
            <span>{item}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

TodoList.defaultProps = { compact: false };

export type RejectionReason = {
  key: string,
  title: string,
  text: string,
  actions: React.ReactNode[],
};

const VISIBLE_REASONS = 3;

// 4fc: motivos en la página (no en un diálogo). Se ven los 3 principales (orden de
// CATEGORY_ORDER) y el resto con "Ver N motivos más". Cada motivo se abre como acordeón con
// sus propios "qué puedes hacer"; el primero parte abierto.
export function ReasonsCard({ reasons }: { reasons: RejectionReason[] }) {
  const [openKey, setOpenKey] = React.useState<string | null>(reasons[0]?.key ?? null);
  const [showAll, setShowAll] = React.useState(false);
  const visible = showAll ? reasons : reasons.slice(0, VISIBLE_REASONS);
  const extra = reasons.length - VISIBLE_REASONS;
  const toggle = (reason: RejectionReason) => {
    const opening = openKey !== reason.key;
    if (opening) {
      analytics.track('Click Button', { text: `Motivo: ${reason.title}`, initiator: 'usuario' });
    }
    setOpenKey(opening ? reason.key : null);
  };
  return (
    <Box
      component="section"
      aria-labelledby="plr-why"
      data-pw="preloan-rejected-reasons"
      sx={{
        background: plr.surface,
        borderRadius: { xs: '20px', sm: '22px' },
        p: { xs: '14px 16px 6px', sm: '18px 22px 8px' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pb: '4px',
      }}
      >
        <Box
          component="h2"
          id="plr-why"
          sx={{
            m: 0,
            fontSize: { xs: 12, sm: 12.5 },
            fontWeight: 700,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: plr.muted,
          }}
        >
          Por qué
        </Box>
        {reasons.length > 1 && (
          <Box component="span" sx={{ fontSize: { xs: 12.5, sm: 13 }, color: plr.muted }}>
            {`${reasons.length} motivos`}
          </Box>
        )}
      </Box>
      {visible.map((reason, i) => {
        const open = openKey === reason.key;
        const panelId = `plr-reason-${reason.key}`;
        return (
          <Box key={reason.key} sx={{ borderTop: i === 0 ? 'none' : '1px solid #E2E5EC' }}>
            <ButtonBase
              data-pw={`preloan-reason-${reason.key}`}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(reason)}
              sx={{
                width: '100%',
                minHeight: 56,
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: '12px', sm: '14px' },
                py: { xs: '12px', sm: '14px' },
                textAlign: 'left',
                fontFamily: plr.font,
                color: plr.ink,
                borderRadius: '12px',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  color: plr.danger,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {alertCircleIcon({ size: 16, strokeWidth: 2.2 })}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1, display: 'flex', flexDirection: 'column', gap: '2px',
                }}
              >
                <Box component="b" sx={{ fontSize: { xs: 14.5, sm: 16 }, lineHeight: 1.35 }}>{reason.title}</Box>
                <Box component="span" sx={{ fontSize: { xs: 13, sm: 14.5 }, lineHeight: 1.45, color: plr.body }}>
                  {reason.text}
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  mt: { xs: '8px', sm: '10px' },
                  color: plr.muted,
                  transition: 'transform .15s',
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              >
                {chevronDownIcon({ size: 18 })}
              </Box>
            </ButtonBase>
            <Collapse in={open}>
              <Box id={panelId} sx={{ pl: { xs: '44px', sm: '50px' }, pb: { xs: '14px', sm: '16px' } }}>
                <TodoList items={reason.actions} compact />
              </Box>
            </Collapse>
          </Box>
        );
      })}
      {extra > 0 && (
        <ButtonBase
          data-pw="preloan-reasons-more"
          aria-expanded={showAll}
          onClick={() => {
            // Al colapsar, cierra el motivo abierto si queda oculto.
            const topKeys = reasons.slice(0, VISIBLE_REASONS).map((r) => r.key);
            if (showAll && openKey && !topKeys.includes(openKey)) setOpenKey(null);
            setShowAll(!showAll);
          }}
          sx={{
            alignSelf: 'flex-start',
            m: '2px 0 8px',
            py: '8px',
            fontFamily: plr.font,
            fontSize: { xs: 13.5, sm: 14 },
            fontWeight: 700,
            color: plr.ink,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            borderRadius: '8px',
          }}
        >
          {showAll ? 'Ver menos motivos' : `Ver ${extra} ${extra === 1 ? 'motivo' : 'motivos'} más`}
        </ButtonBase>
      )}
    </Box>
  );
}

// Cierre de los resultados (4f / 4fb / 4fc): divider y "← Volver a {partner}" centrado.
// ("Cómo lo calculamos" y "Pedir revisión" se quitaron a pedido de diseño; los textos de la
// decisión automatizada siguen en legal.ts por si Legal los pide.)
export function ResultClosing({ backLabel }: { backLabel: string }) {
  return (
    <Box
      data-pw="plr-result-closing"
      sx={{
        mt: { xs: 'auto', sm: '8px' },
        borderTop: `1px solid ${plr.divider}`,
        pt: { xs: '14px', sm: '16px' },
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <PlrBackLink
        type="submit"
        testId="preloan-submit-button"
        sx={{ alignSelf: 'center', ml: 0, fontSize: { xs: 15, sm: 16 } }}
      >
        {backLabel}
      </PlrBackLink>
    </Box>
  );
}

// Decisión automatizada (Ley 21.719, art. 8 bis): el resultado lo calcula el sistema, así que
// se informa cómo y se ofrece pedir que una persona lo revise (explicación, intervención
// humana, punto de vista y revisión). Va antes del cierre en 4f, 4fb y 4fc.
export function DecisionInfo({ project, hasProject = true }: {
  project?: string,
  hasProject?: boolean,
}) {
  const { showNewMessage } = useIntercom();
  const [open, setOpen] = React.useState(false);
  const toggle = () => {
    if (!open) {
      analytics.track('Click Boton', { text: 'Cómo calculamos este resultado', initiator: 'usuario' });
    }
    setOpen(!open);
  };
  const requestReview = () => {
    analytics.track('Click Boton', { text: 'Pedir revisión de una persona', initiator: 'usuario' });
    showNewMessage(HUMAN_REVIEW_MESSAGE(project));
  };
  const linkSx = {
    font: 'inherit',
    fontFamily: plr.font,
    fontWeight: 600,
    color: plr.ink,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    borderRadius: '6px',
    '&:hover': { color: plr.inkHover },
  } as const;
  return (
    <Box
      component="section"
      aria-label="Sobre este resultado"
      data-pw="plr-decision-info"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: { xs: 13, sm: 14 },
        lineHeight: 1.45,
        color: plr.body,
      }}
    >
      <ButtonBase
        data-pw="plr-decision-how"
        aria-expanded={open}
        aria-controls="plr-decision-panel"
        onClick={toggle}
        sx={{
          ...linkSx, alignSelf: 'flex-start', gap: '6px', textDecoration: 'none',
        }}
      >
        Cómo calculamos este resultado
        <Box
          component="span"
          sx={{ display: 'flex', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}
        >
          {chevronDownIcon({ size: 16 })}
        </Box>
      </ButtonBase>
      <Collapse in={open}>
        <Box
          component="ul"
          id="plr-decision-panel"
          sx={{
            m: 0, p: '0 0 2px 18px', display: 'flex', flexDirection: 'column', gap: '6px',
          }}
        >
          {decisionExplanation(hasProject).map((line) => <li key={line}>{line}</li>)}
        </Box>
      </Collapse>
      <Box component="p" sx={{ m: 0 }}>
        ¿No estás de acuerdo?
        {' '}
        <ButtonBase data-pw="plr-human-review" onClick={requestReview} sx={{ ...linkSx, verticalAlign: 'baseline' }}>
          Pide que una persona revise tu evaluación
        </ButtonBase>
        .
      </Box>
    </Box>
  );
}

DecisionInfo.defaultProps = { project: undefined, hasProject: true };
